// DEPENDENCIES
import { FormikValues } from "formik";

// TYPES
import {
  Template,
  PersonType,
  DraftUUID,
  ComponentStep,
  StepFivePF,
  StepSixPF,
} from "../../types";
import { AppDispatch, RootState } from "../../app/store";

// UTILS
import { checkboxRequired, checkbox } from "../../validations";
import { getDraftStepsToSend, handleSendStep } from "../../app/utils";
import { onUpdateFormData } from "../../reducers/onboardingDataReducer";
import getTemplateData from "../../hooks/useTemplate";
import { getCompanyData } from "../../utils/companies";

// STEPS
import infoStep from "../../steps/physical/infoStep";
import identityStep from "../../steps/physical/identityStep";
import basicInformationStep from "../../steps/physical/basicInformationStep";
import emailValidationStep from "../../steps/physical/emailValidationStep";
import employmentProfileStep from "../../steps/physical/employmentProfileStep";
import investmentProfileStep from "../../steps/physical/investmentProfileStep";
import personalInformationStep from "../../steps/physical/personalInformationStep";
import coownersStep from "../../steps/physical/coownersStep";
import preconfirmationSteps, {
  coownersPreconfirmationStep,
} from "../../steps/physical/preconfirmationSteps";

import bodyIcon from "../../assets/images/icons/ui/body.svg";
import { physicalEmailVerificationAuthorize, physicalEmailVerificationSuccess } from "../../utils";

const ownerPreconfirmationSteps = preconfirmationSteps;

const onboardingCoowner = async (
  dispatch: AppDispatch,
  draftUUID?: DraftUUID,
  formValues?: FormikValues | null,
): Promise<Template> => {
  try {
    const queryParams = new URLSearchParams(window.location.search);
    const uuid = queryParams.get("externalCode") || "";

    const {
      favicon,
      formTitle,
      logo,
      questions,
      heritageDocuments,
      countries,
      countriesOptions,
      citizenshipOptions,
      activitiesOptions,
      getProvincesFilteredByCountry,
      provinces,
    } = await getTemplateData({
      draftUUID: draftUUID || uuid,
      formValues,
    });

    if (heritageDocuments?.length) {
      dispatch(
        onUpdateFormData({
          heritageFiles: heritageDocuments,
        }),
      );
    }

    const steps = [
      basicInformationStep("Cuenta cotitular"),
      emailValidationStep({ step: 2 }),
      identityStep(countries),
      personalInformationStep(
        countriesOptions,
        citizenshipOptions,
        getProvincesFilteredByCountry,
        provinces,
      ),
      employmentProfileStep(
        activitiesOptions,
        countriesOptions,
        getProvincesFilteredByCountry,
        provinces,
        undefined,
        true,// hasCoownersStep
      ),
      coownersStep({ isFirstCoowner: true }),
      investmentProfileStep(questions),
    ];

    return {
      onboardingType: "COOWNER",
      formTitle,
      logo,
      icon: bodyIcon,
      favicon,
      steps: [
        ...steps.map((step, index) => ({ step: index + 1, ...step })),
        infoStep({}),
      ],
      onLoad: async ({ draftUUID, dispatch, formik, state }) => {
        if (!draftUUID) return;

        await physicalEmailVerificationAuthorize(
          draftUUID,
          dispatch,
          formik,
          state,
        );
        await physicalEmailVerificationSuccess(dispatch, formik, 2);
      },
      remoteSteps: [
        {
          link: "/dni",
          step: identityStep(countries).subSteps?.find(
            (step) => step.label === "uploadDNI",
          ) as ComponentStep,
        },
        {
          link: "/identidad",
          step: identityStep(countries).subSteps?.find(
            ({ step }) => step === 3,
          ) as ComponentStep,
        },
      ],
      preconfirmation: ({ values }) => ({
        steps: [
          ...ownerPreconfirmationSteps(
            countriesOptions,
            citizenshipOptions,
            activitiesOptions,
            getProvincesFilteredByCountry,
            provinces,
            questions,
            "COOWNER",
          ),
          coownersPreconfirmationStep({ values, isFirstCoowner: true }),
        ],
        checkboxes: getCompanyData(formValues).termsAndConditions.map(
          (term) => ({
            ...term,
            initialValue: false,
            validation: term.required ? checkboxRequired : checkbox,
          }),
        ),
        sendTermsAndConditions: (state: RootState, values: FormikValues) =>
          handleSendTermsAndConditions(state, values, dispatch),
      }),
    };
  } catch (error) {
    throw new Error(error as string);
  }
};

const handleSendTermsAndConditions = async (
  state: RootState,
  values: FormikValues,
  dispatch: AppDispatch,
) => {
  const {
    onboardingData: { sendedSteps, formValues },
  } = state;

  const termsAndConditionsStep = getPreconfirmationStep(values);
  await handleSendStep({
    personType: "PHYSICAL" as PersonType,
    email: formValues?.email,
    sendedSteps,
    stepId: 5,
    step: termsAndConditionsStep,
    dispatch,
  });

  const coownersOnboardingStep: StepSixPF = {
    "@type": "6_PHYSICAL",
    coOwnerSteps: [],
  };

  await handleSendStep({
    personType: "PHYSICAL" as PersonType,
    email: formValues?.email,
    sendedSteps: getDraftStepsToSend(sendedSteps, termsAndConditionsStep),
    stepId: 6,
    step: coownersOnboardingStep,
    dispatch,
  });
};

const getPreconfirmationStep = (values: FormikValues): StepFivePF => {
  const step: StepFivePF = {
    "@type": "5_PHYSICAL",
    acceptedOptionalTermsAndConditions: values?.senebiAndExteriorNotes,
    acceptedTermsAndConditions: values?.termsAndConditions,
  };

  return step;
};

export default onboardingCoowner;
